/**
 * BenchmarkSourceRow: one benchmark of the team. Its source (where the
 * bytes are), its harness (the adapter that runs them) and the eval group
 * it fills. Each row names its own eval group, because a task slug reused
 * in a second group moves the task and its run history out of the first.
 */

import { X } from 'lucide-react'

import type { ConnectionAdminItem, GithubAppStatus } from '@/api/generated/model'
import { RowAction } from '@/components/settings/RowAction'
import { EYEBROW } from '@/components/ui/eyebrow'

import type { EntryRow } from './benchmarkSources'
import { type AdapterChoices, HarnessFields } from './HarnessFields'
import { SourceFields } from './SourceFields'
import { TextField } from './TextField'

interface BenchmarkSourceRowProps {
  index: number
  entry: EntryRow
  github: GithubAppStatus | undefined
  githubPending: boolean
  connections: ConnectionAdminItem[]
  adapters: AdapterChoices
  /** The eval PR layout is saved: open eval PRs are resolved against this repository and this adapter's case file rule. */
  frozen: boolean
  onChange: (entry: EntryRow) => void
  onRemove: () => void
}

export function BenchmarkSourceRow({
  index,
  entry,
  github,
  githubPending,
  connections,
  adapters,
  frozen,
  onChange,
  onRemove,
}: BenchmarkSourceRowProps): React.JSX.Element {
  const n = String(index + 1)
  const id = `benchmark-${n}`
  return (
    <li className="space-y-5 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <div className="flex items-center gap-2">
        <span className={EYEBROW}>Benchmark {n}</span>
        <span className="ml-auto">
          <RowAction label={`Remove benchmark ${n}`} icon={X} onClick={onRemove} destructive />
        </span>
      </div>
      <section className="space-y-3" aria-label={`Benchmark ${n} source`}>
        <p className={EYEBROW}>Source</p>
        <SourceFields
          id={id}
          source={entry.source}
          github={github}
          githubPending={githubPending}
          connections={connections}
          frozenRepo={frozen}
          onChange={(source) => {
            onChange({ ...entry, source })
          }}
        />
      </section>
      <section className="space-y-3" aria-label={`Benchmark ${n} harness`}>
        <p className={EYEBROW}>Harness</p>
        <HarnessFields
          id={id}
          harness={entry.harness}
          adapters={adapters}
          frozenAdapter={frozen}
          onChange={(harness) => {
            onChange({ ...entry, harness })
          }}
        />
      </section>
      <section className="space-y-3" aria-label={`Benchmark ${n} eval group`}>
        <p className={EYEBROW}>Eval group</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            id={`${id}-group`}
            label="Eval group slug"
            value={entry.group.slug}
            mono
            placeholder="acme-benchmark"
            onChange={(slug) => {
              onChange({ ...entry, group: { ...entry.group, slug } })
            }}
          />
          <TextField
            id={`${id}-display`}
            label="Eval group name"
            value={entry.group.display}
            placeholder="Acme Benchmark"
            onChange={(display) => {
              onChange({ ...entry, group: { ...entry.group, display } })
            }}
          />
        </div>
      </section>
    </li>
  )
}
